import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, DurationDeterminingEffector } from "./EffectorBase";
import { CustomEventTypes, PropertyTypes } from '../../../globals';
import Utils from '../../Utilities';
import MaterialAsset from "../../MaterialAsset";
import SliderProperty from '../../../components/properties/SliderProperty';
import { TimelineFragment } from '../TimelineFragment';
import Scenario from '../../scenarioElements/Scenario';

interface EffectorVideoPlaybackSaveData
{
	volume: number;
}

export default class EffectorVideoPlayback extends EffectorBase<EffectorVideoPlayback> implements DurationDeterminingEffector 
{
	isPlaying: boolean;
	volume: number;

	constructor(fragment : TimelineFragment)
	{
		super(fragment);
		this.isPlaying = false;
		this.volume = 1;
	}

	getInitialDuration = () : number => 
	{
		const targetObjects = this.targetObjects();
		if(targetObjects.length > 0)
		{
			const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetObjects[0].userData.guid);
			if (materialAsset && materialAsset.videoDuration)
			{
				return materialAsset.videoDuration;
			}
		}

		return 0;
	}

	save = () : EffectorVideoPlaybackSaveData =>
	{
		const output = {
			volume: this.volume
		};

		return output;
	};

	load = (savedData: EffectorVideoPlaybackSaveData) =>
	{
		if (savedData.volume) 
		{
			this.setVolume(savedData.volume);
		}
	};

	playVideo = () =>
	{
		if (this.fragment.timeline.id !== Scenario.currentStep()?.timeline?.id) return;

		if (!this.isPlaying)
		{
			this.forEachTargetObject((targetObject) =>
			{
				const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetObject.userData.guid);
				if (materialAsset)
				{
					const materialInstance = MaterialAsset.getTimelineMaterialInstance(targetObject);
					if (materialInstance)
					{
						const videoElement = materialInstance.videoElementClone;
						if (videoElement && !materialInstance.videoTimelineOverride)
						{
							videoElement.volume = this.volume;
							videoElement.currentTime = 0;
							videoElement.play();
						}
					}
				}
			});

			this.isPlaying = true;
		}
	}

	stopVideo = () =>
	{
		if (this.isPlaying)
		{
			this.forEachTargetObject(function(targetObject)
			{
				const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetObject.userData.guid);
				if (materialAsset)
				{
					const materialInstance = MaterialAsset.getTimelineMaterialInstance(targetObject);
					if (materialInstance)
					{
						const videoElement = materialInstance.videoElementClone;
						if (videoElement && !materialInstance.videoTimelineOverride)
						{
							videoElement.pause();
							videoElement.currentTime = 0;
						}
					}
				}
			});

			this.isPlaying = false;
		}
	}

	setVolume = (volume : number) =>
	{
		this.volume = volume;
	}

	copyValues = (sourceEffector: EffectorVideoPlayback) =>
	{
		this.volume = sourceEffector.volume;
	}

	resetState = () =>
	{

	}

	update = (fragmentTime: number) =>
	{
		if (Utils.IsSimulationRunning())
		{
			if (this.fragment.previousTime > fragmentTime) this.stopVideo();

			if (fragmentTime > 0 && fragmentTime < 1) 	this.playVideo();
			else this.stopVideo();
		}
		else
		{
			this.forEachTargetObject((targetObject) =>
			{
				const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetObject.userData.guid);
				if (materialAsset)
				{
					const materialInstance = MaterialAsset.getTimelineMaterialInstance(targetObject);
					if (materialInstance)
					{
						const videoElement = materialInstance.videoElementClone;
						if (videoElement && videoElement.duration)
						{
							videoElement.currentTime = (fragmentTime * this.fragment.getDuration()) % videoElement.duration;
						}
					}
				}
			});
		}
	}

	onAdded = () =>
	{
		this.forEachTargetObject(function(targetObject)
		{
			const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(targetObject.userData.guid);
			if (materialAsset)
			{
				const object = targetObject as THREE.Mesh;
				const material = object.material as THREE.Material;
				let materialInstance = MaterialAsset.getTimelineMaterialInstance(targetObject);
				if(!materialInstance)
				{
					if(material.userData.guid)
					{
						materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, material);
					}
					else
					{
						materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, materialAsset.internalMaterial as THREE.Material);

					}
				}

				materialInstance.assignVideoElementClone();
			}
		});

		document.addEventListener(CustomEventTypes.simulationStopped, this.stopVideo);
		document.addEventListener(CustomEventTypes.onUnloadScene, this.stopVideo);
	}

	onDelete = () =>
	{
		this.stopVideo();
		super.onDelete();
		document.removeEventListener(CustomEventTypes.simulationStopped, this.stopVideo);
		document.removeEventListener(CustomEventTypes.onUnloadScene, this.stopVideo);
		this.isPlaying = false;
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Volume",
				PropertyTypes.Slider,
				<SliderProperty
					min={0.0}
					max={1.0}
					step={0.01}
					value={this.volume}
				/>,
				() => {return this.volume;},
				(v) => {this.setVolume(v);},
			),
		];
	}
}
